import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../constants';
import { useTranslation } from 'react-i18next';
import { createPlaceholderUrl } from '../../../utils/PlaceholderCreator';

const CostumeSkill = ({ cardId, refreshTrigger }) => {
    const { t } = useTranslation();
    const [skills, setSkills] = useState([]);
    const { isAdmin, token } = useAuth();
    const isUserAdmin = isAdmin();

    useEffect(() => {
        const fetchCardSkills = async () => {
            try {
                // Determine headers for request
                const response = await fetch(`${API_BASE_URL}/card_skills/${cardId}`);

                if (response.ok) {
                    const data = await response.json();
                    setSkills(data);
                }
            } catch (error) {
                console.error("Error fetching card skills:", error);
            }
        };

        if (cardId) {
            fetchCardSkills();
        }
    }, [cardId, refreshTrigger]);

    const handleDelete = async (id) => {
        if (!window.confirm(t('CostumeSkill.confirm_delete'))) return;

        try {
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            const response = await fetch(`${API_BASE_URL}/card_skills/${id}`, {
                method: 'DELETE',
                headers: headers
            });
            if (response.ok) {
                // Remove from local state
                setSkills(prev => prev.filter(s => s.id !== id));
            }
        } catch (error) {
            console.error("Error deleting skill:", error);
        }
    };

    const getSkillIcon = (iconId) => {
        return `/images/Icon/Skill_Icons/icon_skill_${iconId}.png`;
    };

    // Group skills by category
    const groupedSkills = {
        'Unique': skills.filter(s => s.Category === 'Unique'),
        'Innate': skills.filter(s => s.Category === 'Innate'),
        'Awakening': skills.filter(s => s.Category === 'Awakening'),
        'Events': skills.filter(s => s.Category === 'Events')
    };

    const renderSkillGroup = (title, groupSkills) => {
        if (groupSkills.length === 0) return null;

        return (
            <div className="mb-4 last:mb-0">
                <h4 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 border-b border-gray-100 dark:border-gray-700 pb-1">
                    {title}
                </h4>
                <div className="space-y-2">
                    {groupSkills.map(skill => (
                        <div key={skill.id} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg group relative border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors">
                            <img
                                src={getSkillIcon(skill.icon_id)}
                                alt=""
                                className="w-10 h-10 rounded shadow-sm"
                                onError={(e) => e.target.src = createPlaceholderUrl(40, 40, '?')}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm truncate">
                                        {skill.Skill_name}
                                    </span>
                                    {/* Category Specific Badges */}
                                    {skill.Category === 'Unique' && (
                                        <span className="px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs rounded font-medium">
                                            ★ {skill.star}
                                        </span>
                                    )}
                                    {skill.Category === 'Awakening' && (
                                        <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded font-medium">
                                            Lv {skill.star}
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{skill.Skill_name_jp}</div>
                            </div>

                            {/* Admin Delete Button */}
                            {isUserAdmin && (
                                <button
                                    onClick={() => handleDelete(skill.id)}
                                    className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity"
                                    title={t('CostumeSkill.tooltip_delete')}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (skills.length === 0) return null;

    return (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            {renderSkillGroup(t('CostumeSkill.group_unique'), groupedSkills['Unique'])}
            {renderSkillGroup(t('CostumeSkill.group_innate'), groupedSkills['Innate'])}
            {renderSkillGroup(t('CostumeSkill.group_awakening'), groupedSkills['Awakening'])}
            {renderSkillGroup(t('CostumeSkill.group_events'), groupedSkills['Events'])}
        </div>
    );
};

export default CostumeSkill;
// Revisado: 2025-12-18 [I18n Scan]
